import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import {
  Ambulance,
  Banknote,
  BarChart2,
  BarChart3,
  Bike,
  Bookmark,
  Brain,
  Circle,
  CircleAlert,
  ArrowDown,
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowRight,
  ArrowRightCircle,
  ArrowUpNarrowWide,
  Bell,
  Bot,
  AtSign,
  Braces,
  Bus,
  Car,
  CarTaxiFront,
  Briefcase,
  Bug,
  Building2,
  Calendar,
  CalendarClock,
  CalendarCheck,
  Calculator,
  Camera,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Cpu,
  Compass,
  Copy,
  CornerDownLeft,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ChevronRight,
  ChevronsUpDown,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Columns3,
  Database,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  Factory,
  EyeOff,
  FileText,
  Filter,
  Funnel,
  Flag,
  FlaskConical,
  Folder,
  Fuel,
  Globe,
  Gavel,
  GripVertical,
  Hash,
  Headset,
  Heart,
  Home,
  Hospital,
  Info,
  Image,
  Key,
  Inbox,
  Languages,
  KanbanSquare,
  LayoutGrid,
  Layers,
  List,
  ListFilter,
  ListTodo,
  Lightbulb,
  LoaderCircle,
  ListChecks,
  Loader2,
  Lock,
  Megaphone,
  LogOut,
  Mail,
  Map as MapIcon,
  MapPinned,
  Maximize2,
  MessageCircle,
  MessageSquare,
  MessageSquarePlus,
  MoreHorizontal,
  MoveHorizontal,
  MousePointer,
  Monitor,
  Motorbike,
  Moon,
  Navigation,
  Package,
  Palette,
  Paperclip,
  PartyPopper,
  Percent,
  PieChart,
  PauseCircle,
  Pencil,
  PhoneCall,
  Pin,
  Phone,
  Pill,
  Plane,
  PersonStanding,
  PlayCircle,
  Plus,
  QrCode,
  Ship,
  ShoppingCart,
  RefreshCw,
  Route as RouteIcon,
  Rocket,
  Scale,
  Search,
  Send,
  Settings,
  Settings2,
  Shield,
  ShieldCheck,
  Sigma,
  SlidersHorizontal,
  Smile,
  Sparkles,
  SquarePen,
  Star,
  Stethoscope,
  Store,
  Sun,
  Tag,
  Target,
  Timer,
  Toolbox,
  TrendingDown,
  TrendingUp,
  Type,
  Tractor,
  CheckCircle2,
  Ban,
  Turntable,
  Trash2,
  TrainFront,
  TrainTrack,
  TramFront,
  TriangleAlert,
  Truck,
  TruckElectric,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  UserRound,
  UserSearch,
  Users,
  UsersRound,
  UserX,
  Upload,
  Van,
  Wifi,
  Wallet,
  Warehouse,
  Workflow,
  Wrench,
  X,
  Zap,
  LucideAngularModule,
} from 'lucide-angular';

import { routes } from './app.routes';
import {
  AuthService,
  CompanyContextService,
  companyInterceptor,
  I18nService,
  ThemeService,
  apiBaseInterceptor,
  httpDiagnosticInterceptor,
  authInterceptor,
  paymentRequiredInterceptor,
  unauthorizedInterceptor,
  refreshTokenInterceptor,
} from './core';

function initTheme(theme: ThemeService): () => void {
  return () => theme.initTheme();
}

function initAuth(auth: AuthService): () => void {
  return () => auth.restoreSession();
}

function initTenant(companyContext: CompanyContextService, auth: AuthService): () => Promise<void> {
  return async () => {
    auth.restoreSession();
    if (!auth.isAuthenticated()) {
      companyContext.resetContext();
      return;
    }
    auth.applyRoleFromCurrentAccessToken();
    await companyContext.initCompanyContext();
  };
}

function initI18n(i18n: I18nService): () => Promise<void> {
  return () => i18n.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        apiBaseInterceptor,
        httpDiagnosticInterceptor,
        authInterceptor,
        companyInterceptor,
        paymentRequiredInterceptor,
        unauthorizedInterceptor,
        refreshTokenInterceptor,
      ])
    ),
    { provide: APP_INITIALIZER, useFactory: initTheme, deps: [ThemeService], multi: true },
    { provide: APP_INITIALIZER, useFactory: initAuth, deps: [AuthService], multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initTenant,
      deps: [CompanyContextService, AuthService],
      multi: true,
    },
    { provide: APP_INITIALIZER, useFactory: initI18n, deps: [I18nService], multi: true },
    importProvidersFrom(
      LucideAngularModule.pick({
        Circle,
        CircleAlert,
        ArrowDown,
        ArrowDownWideNarrow,
        ArrowLeft,
        ArrowRight,
        ArrowRightCircle,
        ArrowUpNarrowWide,
        Ambulance,
        BarChart2,
        BarChart3,
        Bookmark,
        AtSign,
        Brain,
        Bot,
        Braces,
        Bell,
        Briefcase,
        Building2,
        Calculator,
        Calendar,
        CalendarClock,
        CalendarCheck,
        Camera,
        Clipboard,
        Clock,
        Cloud,
        Code,
        Cpu,
        Car,
        CarTaxiFront,
        Bus,
        Bike,
        Truck,
        Plane,
        Ship,
        TrainFront,
        TramFront,
        TriangleAlert,
        Check,
        CheckSquare,
        ChevronDown,
        ChevronLeft,
        ChevronUp,
        ChevronRight,
        ChevronsUpDown,
        ClipboardCheck,
        ClipboardList,
        Columns3,
        Compass,
        Copy,
        CornerDownLeft,
        CreditCard,
        Database,
        DollarSign,
        Download,
        ExternalLink,
        Eye,
        EyeOff,
        Factory,
        FileText,
        Filter,
        Funnel,
        Flag,
        FlaskConical,
        Folder,
        Fuel,
        Globe,
        Gavel,
        GripVertical,
        Hash,
        Headset,
        Heart,
        Home,
        Hospital,
        Info,
        Image,
        Key,
        Inbox,
        Languages,
        KanbanSquare,
        LayoutGrid,
        Layers,
        List,
        ListFilter,
        ListTodo,
        Lightbulb,
        LoaderCircle,
        ListChecks,
        Loader2,
        Lock,
        Megaphone,
        LogOut,
        Mail,
        Map: MapIcon,
        MapPinned,
        Maximize2,
        MessageCircle,
        MessageSquare,
        MessageSquarePlus,
        MoreHorizontal,
        MoveHorizontal,
        MousePointer,
        Monitor,
        Motorbike,
        Moon,
        Navigation,
        Package,
        Palette,
        Paperclip,
        PartyPopper,
        Percent,
        PieChart,
        PauseCircle,
        Pencil,
        Phone,
        PhoneCall,
        Pin,
        Pill,
        PersonStanding,
        PlayCircle,
        Plus,
        QrCode,
        ShoppingCart,
        Settings,
        Settings2,
        Sigma,
        SlidersHorizontal,
        Smile,
        Sparkles,
        SquarePen,
        Star,
        Stethoscope,
        Store,
        Bug,
        Toolbox,
        Tractor,
        Turntable,
        Trash2,
        TrainTrack,
        RefreshCw,
        Route: RouteIcon,
        Rocket,
        Scale,
        Search,
        Send,
        Shield,
        ShieldCheck,
        Sun,
        Tag,
        Target,
        Timer,
        TrendingDown,
        TrendingUp,
        Type,
        CheckCircle2,
        Ban,
        Wallet,
        Warehouse,
        Workflow,
        Wrench,
        Banknote,
        TruckElectric,
        User,
        UserCheck,
        UserCog,
        UserPlus,
        UserRound,
        UserSearch,
        Users,
        UsersRound,
        UserX,
        Upload,
        Van,
        Wifi,
        X,
        Zap,
      })
    ),
  ],
};
